import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import * as L from 'leaflet';
import { AlertService } from '../../services/alert.service';
import { MapService } from '../../services/map.service';

@Component({
    selector: 'app-new-address',
    templateUrl: './new-address.component.html',
    styleUrls: ['./new-address.component.scss'],
})
export class NewAddressComponent implements OnInit, OnChanges {
    mapId = 'newMap-' + Math.random().toString(36).substring(2, 9); //Id unico para los mapas
    form: FormGroup;
    map: L.Map;
    marker: L.Marker;
    search: string;
    municipios: Array<{ id: number; nombre: string }> = [];
    @Output() latitudChange = new EventEmitter<number>();
    @Output() longitudChange = new EventEmitter<number>();
    @Output() direccionChange = new EventEmitter<string>();
    @Output() localidadChange = new EventEmitter<string>();
    @Output() upzChange = new EventEmitter<string>();
    @Output() barrioChange = new EventEmitter<string>();
    @Output() esBogotaChange = new EventEmitter<boolean>();
    @Output() municipioChange = new EventEmitter<number>();
    @Input() lat: number | null;
    @Input() lng: number | null;
    @Input() direccion_guardada: string | null;
    @Input() localidad_guardada: string | null;
    @Input() upz_guardada: string | null;
    @Input() barrio_guardado: string | null;
    customIcon = L.divIcon({
        className: 'custom-marker',
        html: `
        <svg xmlns="http://www.w3.org/2000/svg" aria-label="Ubicación seleccionada" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
          </svg>
      `,
        iconSize: [30, 30],
    });
    private readonly DEFAULT_LAT = 4.711;
    private readonly DEFAULT_LNG = -74.0721;

    public tiposVia: Array<{ nombre: string; abreviatura: string }> = [
        { nombre: 'Autopista', abreviatura: 'AUTOP' },
        { nombre: 'Avenida', abreviatura: 'AV' },
        { nombre: 'Avenida Calle', abreviatura: 'AC' },
        { nombre: 'Avenida Carrera', abreviatura: 'AK' },
        { nombre: 'Calle', abreviatura: 'CL' },
        { nombre: 'Callejón', abreviatura: 'CLJ' },
        { nombre: 'Camino', abreviatura: 'CN' },
        { nombre: 'Carrera', abreviatura: 'KR' },
        { nombre: 'Carretera', abreviatura: 'CT' },
        { nombre: 'Circular', abreviatura: 'CIR' },
        { nombre: 'Circunvalar', abreviatura: 'CCV' },
        { nombre: 'Diagonal', abreviatura: 'DG' },
        { nombre: 'Kilómetro', abreviatura: 'KM' },
        { nombre: 'Pasaje', abreviatura: 'PSJ' },
        { nombre: 'Transversal', abreviatura: 'TV' },
        { nombre: 'Troncal', abreviatura: 'TRL' },
        { nombre: 'Vía', abreviatura: 'VIA' },
    ];

    public letras: Array<string> = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
    public referenciaCardinal: Array<string> = ['NORTE', 'SUR', 'ESTE', 'OESTE', 'OCCIDENTE', 'ORIENTE'];

    constructor(
        private alertService: AlertService,
        private mapService: MapService
    ) {}

    ngOnInit(): void {
        this.createForm();
        this.cargarMunicipios();
    }

    private async cargarMunicipios(): Promise<void> {
        try {
            await this.mapService.getMunicipios();
            const data: Array<{ id: number; name: string; state_name: string }> = JSON.parse(localStorage.getItem('param-040') ?? '[]');
            this.municipios = data.map((m) => ({ id: m.id, nombre: `${m.state_name} - ${m.name}` }));
        } catch (error) {
            console.error('Error cargando municipios:', error);
        }
    }

    ngAfterViewInit(): void {
        this.lat = this.form.get('latitud')?.value;
        this.lng = this.form.get('longitud')?.value;
        this.map = L.map(this.mapId).setView([this.form.value.latitud, this.form.value.longitud], 14);
        var basemaps = {
            Calles: L.tileLayer(`https://{s}-tiles.locationiq.com/v2/obk/r/{z}/{x}/{y}.png?key=${environment.API_KEY_GEO}`),
            Estándar: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'),
        };

        L.control.layers(basemaps).addTo(this.map);
        basemaps.Calles.addTo(this.map);

        this.marker = L.marker([this.lat ?? this.DEFAULT_LAT, this.lng ?? this.DEFAULT_LNG], { icon: this.customIcon, draggable: true }).addTo(this.map);
        this.marker.dragging?.enable();
        this.marker.on('dragend', this.onMarkerDragEnd.bind(this));

        /*if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    this.map.setView([latitude, longitude], 14);
                    this.marker.setLatLng([latitude, longitude]);
                    await this.onMarkerDragEnd({ target: this.marker });
                },
                (error) => {}
            );
        } else {
            this.alertService.openDangerNotification('Alerta', 'Su navegador no es compatible con geolocalización');
        }*/
        // Forzar que el mapa se repinte correctamente tras renderizar el componente
        setTimeout(() => {
            if (this.map) {
                this.map.invalidateSize();
            }
        }, 300);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.map && (changes['lat'] || changes['lng'] || changes['direccion_guardada'])) {
            this.loadData();
        }
    }

    createForm() {
        this.form = new FormGroup({
            es_bogota: new FormControl(true, [Validators.required]),

            // --- Sección Bogotá (mapa) ---
            latitud: new FormControl(this.lat ?? 4.655004),
            longitud: new FormControl(this.lng ?? -74.06791),
            direccion_aproximada: new FormControl(this.direccion_guardada ?? undefined),
            localidad: new FormControl(this.localidad_guardada ?? undefined),
            upz: new FormControl(this.upz_guardada ?? undefined),
            barrio: new FormControl(this.barrio_guardado ?? undefined),

            // --- Sección fuera de Bogotá (nomenclatura catastral) ---
            municipio: new FormControl(undefined),
            via: new FormControl(undefined),
            numero_via_principal: new FormControl(undefined),
            letra_via_principal: new FormControl(undefined),
            bis: new FormControl(false),
            cuadrante_via: new FormControl(undefined), // Norte/Sur/Este/Oeste/Occidente/Oriente
            numero_via_generadora: new FormControl(undefined),
            letra_via_generadora: new FormControl(undefined),
            numero_placa: new FormControl(undefined),
            cuadrante_placa: new FormControl(undefined),
            informacion_complementaria: new FormControl(undefined),
        });

        this.form.get('es_bogota')!.valueChanges.subscribe((esBogota: boolean) => {
            this.toggleModoDireccion(esBogota);
            this.esBogotaChange.emit(esBogota);
        });
        this.form.get('municipio')!.valueChanges.subscribe((id: number) => this.municipioChange.emit(id));

        const camposDireccionNacional = ['via', 'numero_via_principal', 'letra_via_principal', 'bis', 'cuadrante_via', 'numero_via_generadora', 'letra_via_generadora', 'numero_placa', 'cuadrante_placa', 'informacion_complementaria'];
        camposDireccionNacional.forEach((campo) => {
            this.form.get(campo)!.valueChanges.subscribe(() => {
                if (this.form.get('es_bogota')?.value === false) {
                    this.direccionChange.emit(this.direccionNacional);
                }
            });
        });

        this.toggleModoDireccion(true);
    }

    private toggleModoDireccion(esBogota: boolean): void {
        const camposBogota = ['direccion_aproximada'];
        const camposNacional = ['municipio', 'via', 'numero_via_principal', 'numero_via_generadora', 'numero_placa'];

        camposBogota.forEach((campo) => this.setRequerido(campo, esBogota));
        camposNacional.forEach((campo) => this.setRequerido(campo, !esBogota));

        if (esBogota) {
            // El contenedor del mapa ya no se destruye con el toggle (ver template: [hidden] en vez de *ngIf),
            // solo estuvo con display:none, así que Leaflet necesita recalcular su tamaño al volver a mostrarse.
            setTimeout(() => this.map?.invalidateSize(), 0);
        }
    }

    private setRequerido(campo: string, requerido: boolean): void {
        const control = this.form.get(campo);
        requerido ? control?.enable({ emitEvent: false }) : control?.disable({ emitEvent: false });
        control?.setValidators(requerido ? [Validators.required] : []);
        control?.updateValueAndValidity({ emitEvent: false });
    }

    get direccionNacional(): string {
        const f = this.form.value;
        const via = this.tiposVia.find((v) => v.nombre === f.via)?.abreviatura ?? '';

        const partes = [via, `${f.numero_via_principal ?? ''}${f.letra_via_principal ?? ''}`, f.bis ? 'Bis' : '', f.cuadrante_via, `${f.numero_via_generadora ?? ''}${f.letra_via_generadora ?? ''}`, f.numero_placa, f.cuadrante_placa];

        return partes.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
    }

    async searchByAddress() {
        this.search = encodeURIComponent(this.form.get('direccion_aproximada')?.value);
        const url = `https://catalogopmb.catastrobogota.gov.co/PMBWeb/web/api?cmd=geocodificar&apikey=${environment.API_KEY_IDECA}&query=${this.search}`;
        try {
            const response = await fetch(url);
            const responseJson = await response.json();
            this.map.setView([responseJson.response.data.latitude, responseJson.response.data.longitude], 14);
            this.marker.setLatLng([responseJson.response.data.latitude, responseJson.response.data.longitude]);
            this.marker.bindPopup(responseJson.response.data.dirinput).openPopup();
            if (!responseJson.response?.success) {
                console.error('Geocodificación falló:', responseJson.response?.message);
            }

            this.form.get('latitud')?.setValue(responseJson.response.data.latitude);
            this.latitudChange.emit(responseJson.response.data.latitude);
            this.form.get('longitud')?.setValue(responseJson.response.data.longitude);
            this.longitudChange.emit(responseJson.response.data.longitude);
            this.form.get('direccion_aproximada')?.setValue(responseJson.response.data.dirtrad);
            this.direccionChange.emit(responseJson.response.data.dirtrad);
            this.form.get('localidad')?.setValue(responseJson.response.data.localidad);
            this.localidadChange.emit(responseJson.response.data.localidad);
            this.form.get('upz')?.setValue(responseJson.response.data.nomupz);
            this.upzChange.emit(responseJson.response.data.nomupz);
            this.form.get('barrio')?.setValue(responseJson.response.data.nomseccat);
            this.barrioChange.emit(responseJson.response.data.nomseccat);
        } catch (error) {
            console.error('Error fetching geocoding data:', error);
            this.alertService.openDangerNotification(
                'Error',
                'No se encontraron resultados para la dirección ingresada. Por favor, verifique que la dirección sea correcta e inténtelo nuevamente. Si la dirección es válida, también puedes seleccionar la ubicación haciendo clic directamente en el mapa.'
            );
        }
    }

    async onMarkerDragEnd(e: any) {
        const { lat: newLat, lng: newLng } = e.target.getLatLng();

        try {
            const url = `https://catalogopmb.catastrobogota.gov.co/PMBWeb/web/api?cmd=geocodificar_inverso&apikey=1fd54084-e295-4960-829e-c5caa3658f80&LATITUD=${newLat}&LONGITUD=${newLng}`;

            const response = await fetch(url);
            const responseJson = await response.json();

            const data = responseJson.response?.data;

            if (!data?.localidad) {
                this.resetMapToBogota();

                this.alertService.openWarningNotification('Ubicación no válida', 'Esta funcionalidad solo está disponible dentro de Bogotá.');

                return;
            }

            this.form.get('latitud')?.setValue(newLat);
            this.latitudChange.emit(newLat);

            this.form.get('longitud')?.setValue(newLng);
            this.longitudChange.emit(newLng);

            this.marker.setLatLng([newLat, newLng]);

            this.form.get('direccion_aproximada')?.setValue(data.dirtrad);
            this.direccionChange.emit(data.dirtrad);

            this.form.get('localidad')?.setValue(data.localidad);
            this.localidadChange.emit(data.localidad);

            this.form.get('upz')?.setValue(data.nomupz);
            this.upzChange.emit(data.nomupz);

            this.form.get('barrio')?.setValue(data.nomseccat);
            this.barrioChange.emit(data.nomseccat);

            this.marker.bindPopup(data.dirtrad).openPopup();

            this.search = data.dirtrad;
        } catch (error) {
            console.error('Error fetching geocoding data:', error);
            this.resetMapToBogota();
            this.alertService.openWarningNotification('Atención', 'No se encontró dirección para esta ubicación. Esta funcionalidad solo está disponible dentro de Bogotá.');
        }
    }

    loadData() {
        if (this.map && this.marker && this.lat && this.lng) {
            this.marker.setLatLng([this.lat, this.lng]);
            this.marker.bindPopup(this.direccion_guardada ?? '').openPopup();
            this.map.setView([this.lat, this.lng], 14);
            this.form.patchValue({
                latitud: this.lat,
                longitud: this.lng,
                direccion_aproximada: this.direccion_guardada,
            });
        }
    }

    private resetMapToBogota(): void {
        this.marker.setLatLng([this.DEFAULT_LAT, this.DEFAULT_LNG]);

        this.map.setView([this.DEFAULT_LAT, this.DEFAULT_LNG], 12);

        this.limpiarUbicacion();
    }

    private limpiarUbicacion(): void {
        this.form.patchValue({
            latitud: null,
            longitud: null,
            direccion_aproximada: '',
            localidad: '',
            upz: '',
            barrio: '',
        });

        this.search = '';
    }

    async usarMiUbicacion(): Promise<void> {

        if (!navigator.geolocation) {

            this.alertService.openDangerNotification('Alerta', 'Su navegador no es compatible con geolocalización');

            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;

                this.map.setView([latitude, longitude], 14);
                this.marker.setLatLng([latitude, longitude]);

                await this.onMarkerDragEnd({
                    target: this.marker,
                });

            },
            (error) => {

                this.alertService.openWarningNotification('Atención', 'No fue posible obtener tu ubicación actual, activa el permiso de geolocalización en tu navegador.');
            }
        );
    }
}
